import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ProjectOutlined,
    PlusOutlined,
    GithubOutlined,
    SearchOutlined,
    FilterOutlined,
    ArrowLeftOutlined,
    LoadingOutlined,
    FolderOpenOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { projectsAPI } from '@/api/endpoints/projects.js';
import styles from '@/styles/pages/projects/ProjectsList.module.css';

const ProjectsList = () => {
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');

    const statuses = [
        { value: 'all', label: 'Все', color: '#7d8590' },
        { value: 'active', label: 'Активен', color: '#3fb950' },
        { value: 'development', label: 'В разработке', color: '#d29922' },
        { value: 'staging', label: 'Тестирование', color: '#8957e5' },
        { value: 'archived', label: 'Архивирован', color: '#7d8590' }
    ];

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        const response = await projectsAPI.getAll(false);
        const projectsData = response.data || [];
        setProjects(projectsData);
        setLoading(false);
    }, []);

    useEffect(() => {
        document.title = 'Все проекты | DevSpace';
        fetchProjects();
    }, [fetchProjects]);

    // Фильтрация и поиск
    useEffect(() => {
        let filtered = [...projects];

        // Поиск по названию и описанию
        if (searchTerm) {
            filtered = filtered.filter(project =>
                project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // Фильтр по статусу
        if (statusFilter !== 'all') {
            filtered = filtered.filter(project => project.status === statusFilter);
        }

        // Сортировка
        filtered.sort((a, b) => {
            let aVal = a[sortBy];
            let bVal = b[sortBy];

            if (sortBy === 'created_at') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        setFilteredProjects(filtered);
    }, [projects, searchTerm, statusFilter, sortBy, sortOrder]);

    const handleCreateProject = () => {
        navigate('/projects/new');
    };

    const handleProjectClick = (projectUuid) => {
        navigate(`/projects/${projectUuid}`);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const getStatusLabel = (status) => {
        const statusMap = {
            'active': 'Активен',
            'development': 'В разработке',
            'staging': 'Тестирование',
            'archived': 'Архивирован'
        };
        return statusMap[status] || status;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    return (
        <div className={styles.container}>
            <div className={styles.bgGradient}></div>
            <div className={styles.bgGrid}></div>

            <div className={styles.content}>
                {/* Шапка */}
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={handleBack}>
                        <ArrowLeftOutlined /> Назад
                    </button>
                    <h1 className={styles.title}>
                        <ProjectOutlined /> Все проекты
                    </h1>
                    <button className={styles.createButton} onClick={handleCreateProject}>
                        <PlusOutlined /> Создать проект
                    </button>
                </div>

                {/* Фильтры */}
                <div className={styles.filtersPanel}>
                    <div className={styles.searchBox}>
                        <SearchOutlined className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Поиск по названию или описанию..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={styles.searchInput}
                        />
                    </div>

                    <div className={styles.filters}>
                        <div className={styles.filterGroup}>
                            <FilterOutlined className={styles.filterIcon} />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className={styles.filterSelect}
                            >
                                {statuses.map(status => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.filterGroup}>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className={styles.filterSelect}
                            >
                                <option value="created_at">По дате создания</option>
                                <option value="name">По названию</option>
                                <option value="status">По статусу</option>
                            </select>
                        </div>

                        <button
                            className={`${styles.sortButton} ${sortOrder === 'asc' ? styles.sortAsc : styles.sortDesc}`}
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        >
                            {sortOrder === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>
                </div>

                {/* Статистика */}
                <div className={styles.stats}>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{projects.length}</span>
                        <span className={styles.statLabel}>Всего проектов</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>{filteredProjects.length}</span>
                        <span className={styles.statLabel}>Показано</span>
                    </div>
                    <div className={styles.statCard}>
                        <span className={styles.statValue}>
                            {projects.filter(p => p.status === 'active').length}
                        </span>
                        <span className={styles.statLabel}>Активных</span>
                    </div>
                </div>

                {/* Список проектов */}
                <div className={styles.projectsContainer}>
                    {loading ? (
                        <div className={styles.loadingState}>
                            <LoadingOutlined className={styles.loadingSpinner} />
                            <p>Загрузка проектов...</p>
                        </div>
                    ) : filteredProjects.length === 0 ? (
                        <div className={styles.emptyState}>
                            <FolderOpenOutlined className={styles.emptyIcon} />
                            <h3>Проекты не найдены</h3>
                            <p>
                                {searchTerm || statusFilter !== 'all'
                                    ? 'Попробуйте изменить параметры поиска'
                                    : 'Создайте свой первый проект'}
                            </p>
                            {(searchTerm || statusFilter !== 'all') && (
                                <button
                                    className={styles.resetButton}
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('all');
                                    }}
                                >
                                    Сбросить фильтры
                                </button>
                            )}
                            {!searchTerm && statusFilter === 'all' && projects.length === 0 && (
                                <button className={styles.createButtonEmpty} onClick={handleCreateProject}>
                                    <PlusOutlined /> Создать проект
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className={styles.projectsGrid}>
                            {filteredProjects.map(project => (
                                <div
                                    key={project.uuid}
                                    className={styles.projectCard}
                                    onClick={() => handleProjectClick(project.uuid)}
                                >
                                    <div className={styles.projectHeader}>
                                        <div
                                            className={styles.projectIcon}
                                            style={{
                                                background: `var(--color-accent-primary)15`,
                                                color: `var(--color-accent-primary)`
                                            }}
                                        >
                                            <ProjectOutlined />
                                        </div>
                                        <div className={styles.projectActions}>
                                            <button
                                                className={styles.actionButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/projects/edit/${project.uuid}`);
                                                }}
                                                title="Редактировать"
                                            >
                                                <EditOutlined />
                                            </button>
                                            <button
                                                className={styles.actionButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Удаление с подтверждением
                                                    if (window.confirm('Удалить проект?')) {
                                                        projectsAPI.delete(project.uuid).then(() => {
                                                            fetchProjects();
                                                        });
                                                    }
                                                }}
                                                title="Удалить"
                                            >
                                                <DeleteOutlined />
                                            </button>
                                        </div>
                                    </div>

                                    <div className={styles.projectContent}>
                                        <h3 className={styles.projectName}>{project.name}</h3>
                                        {project.description && (
                                            <p className={styles.projectDescription}>
                                                {project.description.length > 120
                                                    ? `${project.description.substring(0, 120)}...`
                                                    : project.description}
                                            </p>
                                        )}
                                        <div className={styles.projectMeta}>
                                            <span
                                                className={styles.projectStatus}
                                                style={{
                                                    backgroundColor: `${statuses.find(s => s.value === project.status)?.color}15`,
                                                    color: statuses.find(s => s.value === project.status)?.color
                                                }}
                                            >
                                                {getStatusLabel(project.status)}
                                            </span>
                                            {project.repository_url && (
                                                <a
                                                    href={project.repository_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={styles.projectRepo}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <GithubOutlined /> Репозиторий
                                                </a>
                                            )}
                                        </div>
                                        <div className={styles.projectDate}>
                                            Создан: {formatDate(project.created_at)}
                                        </div>
                                    </div>

                                    <button
                                        className={styles.viewButton}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleProjectClick(project.uuid);
                                        }}
                                    >
                                        <EyeOutlined /> Открыть
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectsList;